import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { readAdvisorState } from "@/data/polyFitData";
import { trackAdvisorEvent } from "@/utils/analytics";

const TOUR_MS = 5000;

export function FeatureHotspots({ hotspots, productId, productName }) {
  const points = hotspots.points || [];
  const [active, setActive] = useState(0);
  const [auto, setAuto] = useState(true);
  const index = Math.min(active, points.length - 1);
  const point = points[index] || {};
  const touring = auto && points.length > 1 && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const track = (i, source) => trackAdvisorEvent("hotspot_viewed", { sessionId: readAdvisorState().sessionId, product_id: productId, hotspot_index: i + 1, hotspot_title: points[i]?.title || "", source });

  useEffect(() => { track(0, "open"); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!touring) return undefined;
    const timer = setTimeout(() => { const next = (index + 1) % points.length; track(next, "auto"); setActive(next); }, TOUR_MS);
    return () => clearTimeout(timer);
  }, [touring, index, points.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (i) => { setAuto(false); setActive(i); track(i, "tap"); };

  return (
    <div className="hotspot-explorer" data-testid="feature-hotspots" data-touring={touring}>
      <figure className="hotspot-figure" data-testid="feature-hotspots-figure">
        <img src={hotspots.imageUrl} alt={`${productName} in use`} data-testid="feature-hotspots-image" />
        {points.map((pt, i) => (
          <button key={i} type="button" className={`hotspot-dot ${i === index ? "active" : ""}`} style={{ left: `${pt.x}%`, top: `${pt.y}%` }} onClick={() => pick(i)} aria-pressed={i === index} aria-label={pt.title} data-testid={`feature-hotspot-${i + 1}`}>{i + 1}</button>
        ))}
      </figure>
      <aside className="hotspot-panel" key={index} data-testid="feature-hotspot-panel">
        {touring && <div className="hotspot-progress" aria-hidden="true" data-testid="feature-hotspot-progress"><span style={{ animationDuration: `${TOUR_MS}ms` }} /></div>}
        <div className="hotspot-nav">
          <button type="button" onClick={() => pick(index - 1)} disabled={index === 0} aria-label="Previous feature" data-testid="feature-hotspot-prev"><ChevronLeft size={18} /></button>
          <button type="button" className="dark" onClick={() => pick(index + 1)} disabled={index === points.length - 1} aria-label="Next feature" data-testid="feature-hotspot-next"><ChevronRight size={18} /></button>
        </div>
        <h3 data-testid="feature-hotspot-title">{point.title}</h3>
        <p data-testid="feature-hotspot-body">{point.body}</p>
        {point.imageUrl && <img className="hotspot-thumb" src={point.imageUrl} alt="" data-testid="feature-hotspot-thumb" />}
      </aside>
    </div>
  );
}
